import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { getCompany, postCompany, putCompany } from "store/thunks";
import * as Yup from "yup";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import userApi from "api/userApi";
import DropdownOption from "components/Common/DropdownOption";
import NaverMap from "components/Common/NaverMap";
import SearchAddress from "components/Common/SearchAddress";
import { useRole } from "components/Hooks/UserHooks";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import UploadFile from "components/Common/UploadFile";
import addressApi from "api/addressApi";

export interface Tag {
  id: string;
  text: string;
}

export interface Props {
  id?: string;
  triggerRefresh?: () => void;
  onClose?: () => void;
}

registerLocale("en", en);
registerLocale("ko", ko);

const CreateForm = ({ id = "", triggerRefresh, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const [isOpenSearchAddress, setIsOpenSearchAddress] =
    useState<boolean>(false);
  const [isLoadingMap, setLoadingMap] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);
      const data = {
        is_active: values?.is_active ?? 1,
      };

      const response: any = id
        ? await putCompany(id, data)
        : await postCompany(data);
      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    field_en: Yup.string().required(`${t("This field is required")}`),
    field_kr: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      field_en: "",
      field_kr: "",
      status: null,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    const v_nickname = valueDefault?.nickname || "";
    setTimeout(() => {
      formik.setFieldValue("nickname", v_nickname);
    }, 300);
  };

  const handleCloseModalAddress = () => {
    setIsOpenSearchAddress(false);
  };

  const handleOpenSearchAddress = () => {
    setIsOpenSearchAddress(true);
  };

  const getLatLngByAddress = async (address: string) => {
    try {
      if (address) {
        const data: any = await addressApi.getGEOByAddress({
          query: encodeURIComponent(address),
        });

        let dataAddress = data?.data?.addresses;

        if (dataAddress?.length > 0) {
          const responseAddress = dataAddress?.[0];
          let address = "";

          if (responseAddress.address) {
            address = responseAddress.address;
          } else if (responseAddress.jibunAddress) {
            address = responseAddress.jibunAddress;
          } else if (responseAddress.roadAddress) {
            address = responseAddress.roadAddress;
          } else if (responseAddress.englishAddress) {
            address = responseAddress.englishAddress;
          } else if (responseAddress.jibunAddressEnglish) {
            address = responseAddress.jibunAddressEnglish;
          }

          if (address) {
            formik?.setFieldValue("address", address);
          }

          const sido = responseAddress?.addressElements.find((i: any) =>
            i?.types.includes("SIDO")
          );

          const sigungu = responseAddress?.addressElements.find((i: any) =>
            i?.types.includes("SIGUGUN")
          );

          const dongmyun = responseAddress?.addressElements.find((i: any) =>
            i?.types.includes("DONGMYUN")
          );

          const roadname = responseAddress?.addressElements.find((i: any) =>
            i?.types.includes("ROAD_NAME")
          );

          const short_address = [sigungu?.shortName, roadname?.shortName].join(
            " "
          );

          formik?.setFieldValue("short_address", short_address);
          formik?.setFieldValue("region1", sido?.longName || "");
          formik?.setFieldValue("region2", sigungu?.longName || "");
          formik?.setFieldValue("region3", dongmyun?.longName || "");

          if (responseAddress?.x || responseAddress?.y) {
            formik?.setFieldValue("lat", responseAddress?.y);
            formik?.setFieldValue("long", responseAddress?.x);
          }
        }
      }
    } catch (error) {
      //
    }
  };

  const onSetValueAddress = async (data: any) => {
    if (data) {
      setLoadingMap(true);
      const res = await getLatLngByAddress(data);
      setLoadingMap(false);
    }
    setIsOpenSearchAddress(false);
  };

  const onCloseClick = () => {
    onClose && onClose();
  };

  const handleCallAllOption = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const [resDetail]: any = await Promise.all([
        idItem ? getCompany(idItem) : {},
      ]);
      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  useEffect(() => {
    handleCallAllOption(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <React.Fragment>
      {isLoadingDetail && (
        <div
          style={{
            position: "absolute",
            zIndex: 3,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgb(164 164 164 / 36%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner size="sm" color="primary"></Spinner>
        </div>
      )}
      <div>
        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
            <div className="flex-grow-1">
              <Row>
                <Col md={6} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="field_en" className="form-label">
                      {t("Field English")}{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="field_en"
                      type="text"
                      className="form-control"
                      id="field_en"
                      placeholder={t("Enter field english")}
                      value={formik?.values?.field_en}
                      onChange={(event: any) =>
                        formik.setFieldValue("field_en", event?.target?.value)
                      }
                    />
                    {formik.touched.field_en && formik.errors.field_en ? (
                      <div className="text-danger mt-2">
                        {formik.errors.field_en}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md={6} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="field_kr" className="form-label">
                      {t("Field Korean")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="field_kr"
                      type="text"
                      className="form-control"
                      id="field_kr"
                      placeholder={t("Enter field korean")}
                      value={formik?.values?.field_kr}
                      onChange={(event: any) =>
                        formik.setFieldValue("field_kr", event?.target?.value)
                      }
                    />
                    {formik.touched.field_kr && formik.errors.field_kr ? (
                      <div className="text-danger mt-2">
                        {formik.errors.field_kr}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md={6} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="status" className="form-label">
                      {t("Status")} <span className="text-danger">*</span>
                    </Label>
                    <DropdownOption
                      name="status"
                      dataList={[]}
                      placeholder={`${t("Select status")}...`}
                      className="dropdown-status-rounded"
                      classNamePrefix="name-prefix"
                      initialValue={formik?.values?.status}
                      onChangeSelect={(e: any) =>
                        formik.setFieldValue("status", e)
                      }
                      isHasOptionAll={false}
                    />
                    {formik.touched.status && formik.errors.status ? (
                      <div className="text-danger mt-2">
                        {formik.errors.status}
                      </div>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          <Row>
            <Col lg={12}>
              <div className="hstack gap-2 justify-content-end">
                {isHavePermissionRole(
                  ROLES_FOR_APP.USER_UPDATE,
                  userPermissions
                ) && (
                  <button type="submit" className="btn btn-primary fs-14">
                    {isLoading ? (
                      <Spinner size="sm me-2"></Spinner>
                    ) : !!id ? (
                      <i className="ri-login-circle-line align-bottom me-2 "></i>
                    ) : (
                      <i className="ri-add-fill align-bottom me-2"></i>
                    )}
                    {id
                      ? t("Button Update Field Company")
                      : t("Button Create Field Company")}
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </form>
      </div>
      {isOpenSearchAddress && (
        <Modal
          isOpen={!!isOpenSearchAddress}
          centered={true}
          size="lg"
          scrollable={false}
        >
          <ModalHeader toggle={handleCloseModalAddress}>
            {"Search Address"}
          </ModalHeader>
          <ModalBody>
            <SearchAddress onSetValueAddress={onSetValueAddress} />
          </ModalBody>
        </Modal>
      )}
      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default CreateForm;
